import React, { useEffect, useRef, useState } from "react";
import { OWNER_TYPES } from "../lib/ownerType";

// Owner-type checkboxes (#3205): absentee, company, out of state, estate/heirs.
// A property shows if its owner matches ANY checked type. Same dropdown look as
// the lead-type filter (.bbx-dd*).

interface Props {
  /** Checked owner types; empty = all. */
  value: string[];
  onChange: (next: string[]) => void;
}

const OwnerTypeMultiSelect: React.FC<Props> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = (t: string) => onChange(value.includes(t) ? value.filter((v) => v !== t) : [...value, t]);

  const label =
    value.length === 0
      ? "Any owner"
      : value.length === 1
        ? OWNER_TYPES.find((o) => o.value === value[0])?.label ?? "1 owner type"
        : `${value.length} owner types`;

  return (
    <div className="bbx-dd" ref={ref}>
      <button
        type="button"
        className={"lv-chip bbx-dd-btn" + (open ? " bbx-dd-btn--open" : "") + (value.length ? " bbx-dd-btn--on" : "")}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Filter by owner type"
        title="Filter by who owns it. Check several to see any of them."
        onClick={() => setOpen((o) => !o)}
      >
        {label}
        <span className="bbx-dd-caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <div className="bbx-dd-panel" role="group" aria-label="Owner types">
          {OWNER_TYPES.map((o) => (
            <label key={o.value} className="bbx-dd-opt" title={o.hint}>
              <input type="checkbox" checked={value.includes(o.value)} onChange={() => toggle(o.value)} />
              <span>{o.label}</span>
            </label>
          ))}
          {value.length > 0 && (
            <button type="button" className="map-buybox-clear" onClick={() => onChange([])}>
              Clear owner types
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default OwnerTypeMultiSelect;
